import cn from 'classnames';
import {useState} from 'react';
import {CategoryTree} from '__generated__/__types__';
import {MenuIcon} from '@/_application/icons/menu';
import SidebarCategoryItem from '@/_application/themes/kidsplaza-spring/layouts/components/sidebar/kidsplaza-spring.desktop.sidebar-category-item';
import SidebarCategoryLevel2List from '@/_application/themes/kidsplaza-spring/layouts/components/sidebar/kidsplaza-spring.desktop.sidebar-category-level-2-list';
import {CategoryListQuery} from '@/framework/gql/categoryList.graphql';

interface IProps {
    isHeader: boolean;
    categories: CategoryListQuery['categoryList'];
}

function Sidebar({isHeader, categories}: Readonly<IProps>) {
    const sortedCategories: CategoryTree[] = Array.isArray(categories) 
        ? JSON.parse(JSON.stringify(categories)) 
        : [];
    sortedCategories.sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));

    const [currentCategory, setCurrentCategory] = useState<null | CategoryTree>(null);
    const handleSelectCategory = (category: CategoryTree) => {
        setCurrentCategory(category);
    };
    const handleLeave = () => {
        setCurrentCategory(null);
    };

    const sidebarClass = cn(
        'group/level1 top-0 left-0 hover:z-30 w-[236px] relative',
        {
            'px-2 py-1 bg-KPZ_Yellow rounded-tl-[20px] rounded-tr-[20px] hover:bg-white': isHeader,
            'h-auto py-6 bg-white rounded-[20px] hidden lg:block transition-all duration-200': !isHeader,
        },
        {
            'hover:rounded-tr-none hover:rounded-br-none bg-white':
                currentCategory?.children && currentCategory.children.length !== 0,
        },
        {
            'z-30 rounded-tr-none rounded-br-none': !!currentCategory,
        },
    );

    const sidebarHeaderClass = cn('flex items-center cursor-pointer', {
        'text-primary text-base px-2': isHeader,
        'mb-[10px] text-xl px-[22px]': !isHeader,
    });

    const sidebarContent = cn('gap-[10px] text-sm lg:text-base leading-[22px]!', {
        'flex-col group-hover/level1:flex absolute w-full top-7 left-0 bg-white pt-2 pb-4 rounded-bl-[20px] rounded-br-[20px] shadow-md h-[362px]':
            isHeader,
        'hidden bg-white': isHeader && !currentCategory,
        'rounded-br-none flex': isHeader && currentCategory?.children && currentCategory.children.length !== 0,
        'flex flex-col': !isHeader,
    });

    return (
        <>
            {currentCategory && (
                <div
                    className={cn('fixed inset-0 z-20 bg-overlay-gray transition-opacity', {
                        'top-[127px]': isHeader,
                        'top-[91px]': !isHeader,
                    })}></div>
            )}
            <div className="relative">
                <div className={sidebarClass} onMouseLeave={handleLeave}>
                    <div className={sidebarHeaderClass}>
                        <MenuIcon className={cn('h-5 w-5', {'text-Line_Icon': !isHeader})} />
                        <h3
                            className={cn('ml-1.5 font-bold', {
                                'font-extrabold! text-primary': isHeader,
                                'text-KPZ_text_1st': !isHeader,
                            })}>
                            Danh mục
                        </h3>
                    </div>

                    <ul className={sidebarContent}>
                        {sortedCategories?.slice(0, 11).map(category => (
                            <SidebarCategoryItem
                                key={category?.id}
                                category={category}
                                onSelectCategory={handleSelectCategory}
                                isHover={category?.id === currentCategory?.id}
                                isHeader={isHeader}
                            />
                        ))}
                    </ul>
                </div>
                {currentCategory?.children && currentCategory.children.length !== 0 && (
                    <SidebarCategoryLevel2List
                        onHover={handleSelectCategory}
                        onLeave={handleLeave}
                        currentCategory={currentCategory}
                        isHeader={isHeader}
                    />
                )}
            </div>
        </>
    );
}

export default Sidebar;
